/**
 * Lightweight Markdown to HTML parser for blog content rendering.
 * Reused from course-engine/src/lib/markdown.ts
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// H-2: link/image URLs are attacker-controlled (CMS-authored blog/course content
// rendered via dangerouslySetInnerHTML). Only allow schemes that can't execute
// script (http/https/mailto) or same-origin relative/anchor paths — everything
// else (javascript:, data:, vbscript:, protocol-relative //, control-char or
// whitespace-obfuscated schemes) falls back to '#'.
function sanitizeUrl(url: string): string {
  // eslint-disable-next-line no-control-regex
  const stripped = url.replace(/^[\x00-\x20]+/, '');
  const normalized = stripped.toLowerCase();
  const isSafeScheme = /^(https?:|mailto:)/.test(normalized);
  const isSafeRelative =
    stripped.startsWith('/') && !stripped.startsWith('//');
  const isSafeAnchorOrDotSlash = stripped.startsWith('#') || stripped.startsWith('./');
  const safe = isSafeScheme || isSafeRelative || isSafeAnchorOrDotSlash;
  return safe ? escapeHtml(stripped) : '#';
}

function parseInline(text: string): string {
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, src) => `<img src="${sanitizeUrl(src)}" alt="${alt}" class="rounded-lg my-4 max-w-full" loading="lazy" />`)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, href) => `<a href="${sanitizeUrl(href)}" target="_blank" rel="noopener noreferrer" class="text-teal-600 hover:underline">${label}</a>`);
}

function parseCells(row: string): string[] {
  return row.split('|').slice(1, -1).map(c => parseInline(c.trim()));
}

export function markdownToHtml(markdown: string): string {
  if (!markdown) return '';

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const output: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    if (!line) { i++; continue; }

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(escapeHtml(lines[i]));
        i++;
      }
      i++;
      output.push(
        `<pre class="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto my-4 text-sm"><code${lang ? ` class="language-${lang}"` : ''}>${codeLines.join('\n')}</code></pre>`
      );
      continue;
    }

    // Headings
    const hMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const sizes: Record<number, string> = {
        1: 'text-3xl font-bold mt-8 mb-4',
        2: 'text-2xl font-bold mt-7 mb-3',
        3: 'text-xl font-semibold mt-6 mb-2',
        4: 'text-lg font-semibold mt-5 mb-2',
        5: 'text-base font-semibold mt-4 mb-1',
        6: 'text-sm font-semibold mt-4 mb-1',
      };
      output.push(`<h${level} class="${sizes[level]} text-slate-900">${parseInline(hMatch[2])}</h${level}>`);
      i++;
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line)) {
      output.push('<hr class="my-8 border-slate-200">');
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      output.push(`<blockquote class="border-l-4 border-teal-500 pl-4 py-1 my-4 text-slate-600 italic"><p>${parseInline(quoteLines.join(' '))}</p></blockquote>`);
      continue;
    }

    // Table
    if (line.startsWith('|') && lines[i + 1]?.trim().match(/^\|[\s\-:|]+\|/)) {
      const headerCells = parseCells(line);
      i += 2;
      const bodyRows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        bodyRows.push(
          `<tr class="border-b border-slate-200">${parseCells(lines[i]).map(c => `<td class="px-4 py-2 text-sm">${c}</td>`).join('')}</tr>`
        );
        i++;
      }
      output.push(
        `<div class="overflow-x-auto my-4"><table class="w-full border border-slate-200 rounded-lg">` +
        `<thead class="bg-slate-50"><tr>${headerCells.map(h => `<th class="px-4 py-2 text-sm font-medium text-left">${h}</th>`).join('')}</tr></thead>` +
        `<tbody>${bodyRows.join('')}</tbody>` +
        `</table></div>`
      );
      continue;
    }

    // Unordered list
    if (/^[-*+] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+] /.test(lines[i].trim())) {
        items.push(`<li class="ml-4">${parseInline(lines[i].trim().replace(/^[-*+] /, ''))}</li>`);
        i++;
      }
      output.push(`<ul class="list-disc pl-4 my-4 space-y-1 text-slate-700">${items.join('')}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
        items.push(`<li class="ml-4">${parseInline(lines[i].trim().replace(/^\d+\. /, ''))}</li>`);
        i++;
      }
      output.push(`<ol class="list-decimal pl-4 my-4 space-y-1 text-slate-700">${items.join('')}</ol>`);
      continue;
    }

    // Paragraph
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,6} /.test(lines[i].trim()) &&
      !/^[-*+] /.test(lines[i].trim()) &&
      !/^\d+\. /.test(lines[i].trim()) &&
      !lines[i].trim().startsWith('|') &&
      !lines[i].trim().startsWith('```') &&
      !lines[i].trim().startsWith('> ') &&
      !/^[-*_]{3,}$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length) {
      output.push(`<p class="my-4 text-slate-700 leading-relaxed">${parseInline(paraLines.join(' '))}</p>`);
    }
  }

  return output.join('\n');
}
